import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Icon, Spacer, StackView, Typography, Divider } from '@telus-uds/components-web';
import { Cronometer, LockClosed, Firewall } from '@telus-uds/palette-allium/build/web/icons';
import { usePlaidLink } from 'react-plaid-link';

import { linkFormContext } from '../../contexts/linkFormContext';
import { Disclaimers } from '../disclaimers/Disclaimers';
import { Signature } from '../signature/Signature';

export const BankAccountForm = () => {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const {
    plaidLinkToken,
    setPlaidPublicToken,
    applicableDisclaimers,
    disclaimerConsents,
    setDisclaimerConsents,
    setSubmitting,
    setMethodCaptureError,
    signatureRequired,
    setSignature,
  } = useContext(linkFormContext);

  const [signatureValid, setSignatureValid] = useState(!signatureRequired);

  // plaid handlers
  const onSuccess = (publicToken) => {
    setPlaidPublicToken(publicToken);
    setSubmitting(true);
  };

  const onExit = (error, metadata) => {
    if (error === null) return;
    console.error('Plaid Link Error:', error);
    const message = error?.display_message || t('error.plaid');
    setMethodCaptureError(message);
  };

  const onEvent = (eventName, metadata) => {
    console.log('Plaid Link Event:', eventName, metadata);
  };

  // prettier-ignore
  const { open, ready: plaidReady, error: plaidLoadError } = usePlaidLink({
    token: plaidLinkToken,
    onSuccess,
    onExit,
    onEvent,
  });

  useEffect(() => {
    if (plaidLoadError) setMethodCaptureError(t('error.plaidLoad'));
  }, [plaidLoadError]);

  const initPlaid = () => {
    if (plaidReady && !plaidLoadError) {
      setIsProcessing(true);
      open();
    }
  };

  const disableSubmit = () => {
    const disclaimersConsented = Object.values(disclaimerConsents).every((consent) => consent);
    return !disclaimersConsented || !signatureValid || isProcessing;
  };

  const benefits = [
    {
      icon: Cronometer,
      title: t('bankAccountForm.benefits.fast.title'),
      text: t('bankAccountForm.benefits.fast.text'),
    },
    {
      icon: LockClosed,
      title: t('bankAccountForm.benefits.accurate.title'),
      text: t('bankAccountForm.benefits.accurate.text'),
    },
    {
      icon: Firewall,
      title: t('bankAccountForm.benefits.secure.title'),
      text: t('bankAccountForm.benefits.secure.text'),
    },
  ];

  return (
    <Box>
      <Typography tag={'h3'} variant={{ size: 'large', bold: true, colour: 'brand' }}>
        {t('bankAccountForm.title')}
      </Typography>
      <Spacer space={4} />
      <StackView direction={'column'} tokens={{ alignItems: 'start' }} space={4}>
        {benefits.map((benefit, i) => (
          <StackView direction={'row'} space={3} tokens={{ alignItems: 'center', flexShrink: 1 }} key={i}>
            <Icon icon={benefit.icon} variant={{ color: 'brand', size: 'large' }} />
            <StackView direction={'column'} tokens={{ alignItems: 'start', flexShrink: 1 }} space={1}>
              <Typography variant={{ bold: true }} tag={'strong'}>
                {benefit.title}
              </Typography>
              <Typography tag={'p'}>{benefit.text}</Typography>
            </StackView>
          </StackView>
        ))}
      </StackView>
      <Spacer space={4} />
      <Disclaimers
        disclaimers={applicableDisclaimers}
        consents={disclaimerConsents}
        setConsents={setDisclaimerConsents}
      />
      {signatureRequired && <Signature setSignature={setSignature} setSignatureValid={setSignatureValid} />}
      <Button variant={{ priority: 'high' }} disabled={disableSubmit()} onPress={initPlaid}>
        {t('bankAccountForm.button')}
      </Button>
    </Box>
  );
};
